/**
 * Matrix band-set scope (M1, 2026-09-01) — PURE, dependency-free.
 *
 * WHY THIS MODULE EXISTS. Melissa, 2026-08-31: *"the price level's bound
 * should change with different product group but the ui not see anywhere can
 * set this per price level for product group select"*. She was right, and the
 * cause was one line in `GroupMatrixTab.tsx`:
 *
 *     (dataset?.priceGroupBands ?? []).filter((b) => b.productGroupId === undefined)
 *
 * The MATRIX was hard-filtered to the DEFAULT band set while the "Band set"
 * selector underneath it drove only the Price Bands TABLE. So a product
 * group's own bounds could be listed but never seen as columns, and its
 * markups could not be read or edited anywhere. The two halves of the tab
 * answered different questions under one heading.
 *
 * The fix is that ONE selected scope drives the columns, the cells AND the
 * table. That selection rule is the thing a guard has to be able to reach —
 * and the repo's test environment is plain `node` with no DOM
 * (`vitest.config.ts:24`), so a rule living inside the `.tsx` is unreachable
 * by any guard in this repo and a guard that re-declares it asserts the wrong
 * function (`erp-web-gate-reality` §2, the 2026-08-28 precedent). The rule
 * therefore lives HERE — types-only imports plus the band-set helpers, no
 * React and no antd — and the component and the guard import the SAME body.
 * `quickCreatePayload.ts` in this directory is the established precedent.
 *
 * WHAT THIS MODULE DOES NOT DO, stated so it is not read as settled:
 * - It says NOTHING about what a price is banded ON. RULING R-BAND is
 *   untouched: `resolvePriceGroup` still buckets on `breakdown.pricingBase`
 *   with D27's required currency argument. This module decides which set a
 *   SURFACE displays and edits — never what the engine bands on.
 * - It retires and re-cuts nothing (RULING R-AD). A–D stays the coarse layer;
 *   a scoped set is an ADDITIONAL set, and no seeded band's values move.
 * - It does not adjudicate a cover. Half-open / gapless / non-overlapping
 *   lives once, in `bandSetInvariants`, enforced by the store reducers.
 */

import type { PriceGroupBand, PriceSource } from '@/types/api/pricing';
import { bandSetScopeOf } from '@/lib/pricing';

/**
 * Every row of ONE band set, ordered currency-then-lower-bound.
 *
 * This is the population BOTH halves of the tab read: the Price Bands table
 * renders it whole (currency as a COLUMN, per P2's D27 legibility rule), and
 * the matrix takes one currency's slice of it as its columns. One body, so
 * "the table lists it" and "the matrix has a column for it" cannot disagree.
 *
 * Ordering is deterministic and does not depend on dataset order, because the
 * matrix currency defaults to the FIRST currency of the set and a
 * seed-order-dependent default would move when a row is appended.
 */
export function bandsInSet(
  bands: readonly PriceGroupBand[],
  scope: string
): PriceGroupBand[] {
  return bands
    .filter((band) => bandSetScopeOf(band) === scope)
    .sort((a, b) =>
      a.currency === b.currency
        ? a.minInclusive - b.minInclusive
        : a.currency.localeCompare(b.currency)
    );
}

/** The currencies a set actually holds bands in, ascending, de-duplicated. */
export function bandSetCurrencies(
  bands: readonly PriceGroupBand[],
  scope: string
): string[] {
  return Array.from(new Set(bandsInSet(bands, scope).map((band) => band.currency)));
}

/**
 * The currency the matrix shows for a set, given the operator's own pick.
 *
 * AN EXPLICIT PICK IS ALWAYS HONOURED, even when the selected set holds no
 * bands in it. Only the UNPICKED default is derived, and it is derived from
 * the SELECTED SET's own currencies — so switching sets before touching the
 * currency control never lands on a stale foreign currency.
 *
 * ⚠ THIS DELIBERATELY DOES NOT SNAP BACK. An earlier cut of this function
 * overrode a pick the set could not satisfy, to avoid painting an empty grid.
 * That was wrong twice over: it silently refused an explicit action on a
 * control that offers the option, and — because the fallback could always
 * find a currency — it made the matrix's empty state UNREACHABLE, leaving
 * user-visible copy that nothing could render. The honest answer to "this set
 * has no TWD bands" is to SAY SO, with the currencies it does have, beside a
 * control the operator can already see. Not to answer a different question.
 *
 * A set with no bands at all yields `null` — never a currency borrowed from
 * some other set.
 */
export function matrixCurrencyFor(
  currencies: readonly string[],
  picked: string | null
): string | null {
  return picked ?? currencies[0] ?? null;
}

/**
 * The matrix's COLUMN bands: one set × one currency, ascending by lower bound.
 *
 * THE REGRESSION THIS REPLACES: a hard `productGroupId === undefined` filter,
 * which pinned the columns to the default set whatever the selector said.
 * `scope` is the argument precisely so that pinning is unrepresentable here —
 * the only way to get the default set is to ASK for `DEFAULT_BAND_SET_SCOPE`.
 */
export function matrixColumnBands(
  bands: readonly PriceGroupBand[],
  scope: string,
  currency: string | null
): PriceGroupBand[] {
  if (currency === null) return [];
  return bandsInSet(bands, scope)
    .filter((band) => band.currency === currency)
    .sort((a, b) => a.minInclusive - b.minInclusive);
}

/**
 * The markup-rule key one matrix CELL reads and writes: band × source × level.
 *
 * Rules are keyed on the BAND ID, so a scoped set's rules are already separate
 * data — `pg-b` and `pg-b-ele-tl` are both "Group B" and index different
 * rules. That separation is what makes "never the default set's numbers
 * borrowed" true by construction rather than by care, and it is asserted
 * against `markupRuleIndex`'s own key shape in the guard so the two cannot
 * drift.
 */
export function markupCellKey(
  bandId: string,
  source: PriceSource,
  priceLevelId: string
): string {
  return `${bandId}|${source}|${priceLevelId}`;
}

/** One level's entry in a cell edit — `null` markup = no rule for the cell. */
export interface MarkupCellEntry {
  priceLevelId: string;
  markupPct: number | null;
}

/** One rule edit the store's `saveMarkupCell` applies. */
export interface MarkupCellEdit {
  priceGroupId: string;
  source: PriceSource;
  priceLevelId: string;
  markupPct: number | null;
}

/**
 * The rule edits ONE cell save files — every one stamped with THIS band's id.
 *
 * The band is passed as a whole row rather than an id so a caller cannot hand
 * in a letter ("B") or a scope and have it silently mean the default set's
 * band: the only thing that reaches `priceGroupId` is the id of the row the
 * operator actually clicked, in the set the tab is showing.
 */
export function markupCellEdits(
  band: Pick<PriceGroupBand, 'id'>,
  source: PriceSource,
  entries: readonly MarkupCellEntry[]
): MarkupCellEdit[] {
  return entries.map((entry) => ({
    priceGroupId: band.id,
    source,
    priceLevelId: entry.priceLevelId,
    markupPct: entry.markupPct,
  }));
}

/**
 * Does this cell hold NO rule at all for any of the levels shown?
 *
 * The honest empty state for a set that has bands but no ladder yet: the cell
 * renders one 未設定 marker instead of one grey "no rule" chip per level, and
 * emphatically not the default set's figures. A cell with PARTIAL coverage is
 * NOT empty — it renders its real rules beside its real gaps, which is the
 * seeded TWD grid's existing (and correct) behaviour.
 */
export function isCellUnset(
  hasRule: (priceLevelId: string) => boolean,
  priceLevelIds: readonly string[]
): boolean {
  return !priceLevelIds.some((id) => hasRule(id));
}
